import { useEffect, useRef } from 'react';
import { useDataChange } from '@/contexts/DataChangeContext';
import apiClient from '@/services/api-client';
import { apiConfig } from '@/config/api';

interface WatcherConfig {
  endpoint: string;
  pageName: string;
  getDataHash: (data: any) => string;
  requestParams?: Record<string, any>;
  intervalMs?: number;
}

// Configuration for all pages that should be watched
const WATCHERS: WatcherConfig[] = [
  {
    endpoint: apiConfig.endpoints.loans.paginated,
    pageName: 'Loans',
    getDataHash: (data) => {
      return JSON.stringify({
        total: data?.total || 0,
        firstLoanId: data?.items?.[0]?.id || null,
        firstLoanUpdated: data?.items?.[0]?.updated_at || null,
      });
    },
    requestParams: {
      page: 1,
      page_size: 1,
    },
    intervalMs: 5000,
  },
  {
    endpoint: apiConfig.endpoints.launchConfig.current,
    pageName: 'Client',
    getDataHash: (data) => {
      return JSON.stringify({
        valuation_date: data?.valuation_date || null,
        settlement_date: data?.settlement_date || null,
        client: data?.client || null,
        client_legal_name: data?.client_legal_name || null,
        portfolio_fund: data?.portfolio_fund || null,
        sub_portfolio: data?.sub_portfolio || null,
        reporting_period: data?.reporting_period || null,
        mark_frequency: data?.mark_frequency || null,
        updated_at: data?.updated_at || null,
      });
    },
    intervalMs: 5000,
  },
  // Add more watchers for other pages here
  // Example for Admin page:
  // {
  //   endpoint: apiConfig.endpoints.users.getAllInvitations,
  //   pageName: 'Admin',
  //   getDataHash: (data) => {
  //     const items = Array.isArray(data) ? data : (data?.items || []);
  //     return JSON.stringify({
  //       count: items.length,
  //       lastUpdated: items[0]?.created_at || items[0]?.updated_at || null,
  //     });
  //   },
  //   intervalMs: 5000,
  // },
];

/**
 * Global component that watches for data changes on all pages simultaneously
 * Works regardless of which page the user is currently viewing
 */
export const GlobalDataWatcher = () => {
  const { notifyDataChange } = useDataChange();
  const watchersRef = useRef<Map<string, { lastHash: string | null; isInitial: boolean; intervalId: NodeJS.Timeout }>>(new Map());

  useEffect(() => {
    // Initialize watchers for all pages
    WATCHERS.forEach((watcher) => {
      const watcherKey = watcher.endpoint;

      // Skip if watcher already exists
      if (watchersRef.current.has(watcherKey)) {
        return;
      }

      const checkForChanges = async () => {
        try {
          const response = await apiClient.get(watcher.endpoint, {
            params: watcher.requestParams || {},
          });
          const data = response.data;

          // Check for ETag or Last-Modified headers (like in useLoansDataWatcher)
          const etag = response.headers?.['etag'] || response.headers?.['ETag'];
          const lastModified = response.headers?.['last-modified'] || response.headers?.['Last-Modified'];

          // Get hash from the data
          const dataHash = watcher.getDataHash(data);
          
          // Include headers in hash if available (more reliable)
          const fullHash = JSON.stringify({
            dataHash,
            etag: etag || null,
            lastModified: lastModified || null,
          });

          const watcherState = watchersRef.current.get(watcherKey);
          if (!watcherState) return;

          // Debug logging in development
          if (import.meta.env.DEV && watcher.pageName === 'Client') {
            console.log(`[GlobalDataWatcher] ${watcher.pageName} check:`, {
              isInitial: watcherState.isInitial,
              lastHash: watcherState.lastHash,
              currentHash: fullHash,
              dataHash: dataHash,
              hasChanged: watcherState.lastHash !== null && watcherState.lastHash !== fullHash,
              rawData: data,
              etag,
              lastModified,
            });
          }

          // Skip the first check to avoid false positives (when lastHash is null)
          // This matches the logic from useLoansDataWatcher
          if (watcherState.lastHash !== null && watcherState.lastHash !== fullHash) {
            console.log(`[GlobalDataWatcher] Data changed detected for ${watcher.pageName}, notifying...`);
            notifyDataChange(watcher.pageName);
          }

          // Update the hash
          watcherState.lastHash = fullHash;
          watcherState.isInitial = false;
        } catch (error) {
          // Silently fail - don't interrupt user experience
          console.warn(`[GlobalDataWatcher] Failed to check for changes on ${watcher.pageName}:`, error);
        }
      };

      // Initialize watcher state
      watchersRef.current.set(watcherKey, {
        lastHash: null,
        isInitial: true,
        intervalId: setInterval(checkForChanges, watcher.intervalMs || 5000),
      });

      // Initial check after a short delay
      setTimeout(checkForChanges, 1000);
    });

    // Cleanup function
    return () => {
      watchersRef.current.forEach((watcherState) => {
        clearInterval(watcherState.intervalId);
      });
      watchersRef.current.clear();
    };
  }, [notifyDataChange]);

  return null; // This component doesn't render anything
};

